import { useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useSchedulerStore } from '@/stores/scheduler';
import { useSearchStore } from '@/stores/search';
import { useDrawer, useToast } from '@dashhudson/dashing-ui';
import { computed, unref } from 'vue';
import { camelizeKeys } from 'humps';
import enumTypes from '@/app/library/constants';
import {
  AUTO_PUBLISH_ONLY_PLATFORMS,
  fbPostTypes,
  MULTISELECT_CHANNEL_ITEMS,
  PLATFORMS,
  SCHEDULER_DRAWERS,
  snapchatPostTypes,
} from '@/app/scheduler/constants';
import keyBy from 'lodash/keyBy';
import { useMediaStore } from '@/stores/media';
import { handleAddToUnscheduledToasts } from '@/app/scheduler/utils/toasts';

export function useAddToSchedulerActions(selectedMedia) {
  const route = useRoute();
  const authStore = useAuthStore();
  const mediaStore = useMediaStore();
  const schedulerStore = useSchedulerStore();
  const searchStore = useSearchStore();

  const { launchDrawer } = useDrawer();
  const { addToast } = useToast({
    life: 3000,
  });

  const formattedSelectedMedia = computed(() =>
    unref(selectedMedia).map((media) => camelizeKeys(media)),
  );

  function getPostType(channel) {
    if (channel === MULTISELECT_CHANNEL_ITEMS.FACEBOOK) {
      return fbPostTypes.POST;
    }
    if (channel === MULTISELECT_CHANNEL_ITEMS.SNAPCHAT) {
      return snapchatPostTypes.STORY;
    }
    return channel.split(' ').pop().toUpperCase() ?? 'FEED';
  }

  function getPlatformFromChannel(channel) {
    if (channel === MULTISELECT_CHANNEL_ITEMS.X) {
      return PLATFORMS.TWITTER;
    }
    return channel.split(' ')[0].toLowerCase();
  }

  async function createPost(channel, data) {
    const platform = getPlatformFromChannel(channel);
    return schedulerStore.createPost({ platform, ...data });
  }

  function createFormattedPost(mediaDetail, targetChannel) {
    const formattedPost = {
      brandId: authStore.currentBrand.id,
      mediaIds: [mediaDetail.mediaId],
      timestamp: null,
      autoPublish: AUTO_PUBLISH_ONLY_PLATFORMS.includes(getPlatformFromChannel(targetChannel)),
    };

    let title = null;
    let description = null;

    if (mediaDetail.source === enumTypes.FACEBOOK) {
      title = mediaDetail?.sourceData?.title;
      description = mediaDetail?.sourceData?.message;
    } else if (mediaDetail.source === enumTypes.TWITTER) {
      description = mediaDetail?.sourceData?.text;
    } else if (mediaDetail.source === enumTypes.PINTEREST) {
      description = mediaDetail?.sourceData?.note;
    } else {
      const baseCaption = mediaDetail?.sourceData?.caption || '';
      const captionHandle =
        mediaDetail.type === enumTypes.INSTAGRAM_UGC
          ? mediaDetail?.sourceData?.instagramUser?.handle
          : null;

      description = baseCaption + (captionHandle ? ` | @${captionHandle}` : '');
    }

    if (targetChannel === MULTISELECT_CHANNEL_ITEMS.FACEBOOK) {
      formattedPost.title = title;
      formattedPost.message = description;
      formattedPost.postType = fbPostTypes.POST;
    } else if (targetChannel === MULTISELECT_CHANNEL_ITEMS.X) {
      formattedPost.tweetStatus = description;
    } else if (targetChannel === MULTISELECT_CHANNEL_ITEMS.PINTEREST) {
      formattedPost.note = description;
    } else if (targetChannel === MULTISELECT_CHANNEL_ITEMS.LINKEDIN) {
      formattedPost.text = description;
    } else {
      formattedPost.postType = getPostType(targetChannel);
      formattedPost.caption = description;
    }

    return formattedPost;
  }

  async function handleAddToUnscheduled(selectedChannels) {
    let mediaIds = [];
    let successfulCreates = 0;
    const invalidErrorChannels = new Set();
    let hasServerError = false;
    if (route.meta.schedulerRoute) {
      mediaIds = formattedSelectedMedia.value.reduce((acc, media) => {
        acc.push(...media.mediaIds);
        return acc;
      }, []);
    } else {
      mediaIds = formattedSelectedMedia.value.map((media) => media.id);
    }

    // select channels of unscheduled post
    await Promise.all(
      selectedChannels.map(async (channel) => {
        if (route.meta.schedulerRoute) {
          // TODO: implement copy post across channels from scheduled backend
          // add posts from one unscheduled channel to another
          const postMediaList = formattedSelectedMedia.value
            .map((item) => {
              if (!item.media) return null;
              return {
                id: item.mediaIds[0],
                ids: item.mediaIds,
              };
            })
            .filter((item) => item !== null);

          await searchStore.getMediaSourceList({
            brandId: authStore.currentBrand.id,
            globalIds: postMediaList.map((media) => media.id).join(','),
          });
          await Promise.all(
            postMediaList.map(async (media) => {
              const formattedPost = {
                brandId: authStore.currentBrand.id,
                mediaIds: media.ids,
                timestamp: null,
                autoPublish: AUTO_PUBLISH_ONLY_PLATFORMS.includes(getPlatformFromChannel(channel)),
                postType: getPostType(channel),
                caption: searchStore.mediaSourceList?.[media.id]?.sourceData?.caption ?? '',
              };
              try {
                await createPost(channel, formattedPost);
                successfulCreates += 1;
              } catch (error) {
                const status = error?.response?.status;
                if (status === 400) {
                  invalidErrorChannels.add(channel);
                } else if (status >= 500) {
                  hasServerError = true;
                }
              }
            }),
          );
        } else {
          // add multiple media from media list
          await searchStore.getMediaSourceList({
            brandId: authStore.currentBrand.id,
            globalIds: mediaIds.join(','),
          });
          const mediaDetailDict = keyBy(searchStore.mediaSourceList.data, (media) => media.mediaId);
          return Promise.all(
            mediaIds.map(async (mediaId) => {
              const mediaDetail = mediaDetailDict?.[mediaId];
              const formattedPost = createFormattedPost(mediaDetail, channel);
              try {
                await createPost(channel, formattedPost);
                successfulCreates += 1;
              } catch (error) {
                const status = error?.response?.status;
                if (status === 400) {
                  invalidErrorChannels.add(channel);
                } else if (status >= 500) {
                  hasServerError = true;
                }
              }
              return mediaId;
            }),
          );
        }
        return undefined;
      }),
    );
    schedulerStore.clearSelectedPosts();

    handleAddToUnscheduledToasts({
      addToast,
      successfulCreates,
      invalidErrorChannels,
      hasServerError,
      mediaIds,
      channels: selectedChannels.map((channel) => channel.id),
    });
  }

  function isNonMediaPost(post) {
    if (post?.source === 'FACEBOOK_LINK') {
      return false;
    }
    return post?.source?.includes('_TEXT') || post?.source?.includes('_LINK');
  }

  async function handleSchedulePinterest() {
    const mediaToSchedule = formattedSelectedMedia.value
      .map((item) => {
        if (isNonMediaPost(item)) return null;
        // item can be media object or post object, only post has media_ids field
        if (item.mediaIds != null) {
          // get media id from post object
          if (item.media && item.media.length > 0) {
            return { id: item.media[0].id };
          }
          return null;
        }
        // return self if it is media object
        return item;
      })
      .filter((item) => item !== null);

    // Validate that media is in MediaV2 format before passing media to the scheduler popup
    const mediaList = await mediaStore.validateMediaList({
      brandId: authStore.currentBrand.id,
      mediaArrList: mediaToSchedule,
    });

    launchDrawer({
      name: SCHEDULER_DRAWERS.BULK_PINTEREST_DRAWER,
      props: {
        mediaList: (mediaList ?? []).map((m) => ({ id: m?.id })),
      },
    });
  }

  return { handleAddToUnscheduled, handleSchedulePinterest };
}
